import React from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import StraightenIcon from "@mui/icons-material/Straighten";
import ScaleIcon from "@mui/icons-material/Scale";
import HistoryIcon from "@mui/icons-material/History";

const SectionHeader = ({ icon, title }) => (
  <Grid item xs={12}>
    <Box display="flex" alignItems="center" gap={0.75} mt={1.5} mb={0.25}>
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        letterSpacing={0.8}
        textTransform="uppercase"
      >
        {title}
      </Typography>
      <Box flex={1} height="1px" bgcolor="divider" ml={0.5} />
    </Box>
  </Grid>
);

const FieldCard = ({ label, value, xs = 3 }) => (
  <Grid item xs={xs}>
    <Box
      sx={{
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
        height: "100%",
        transition: "all 0.15s ease",
        "&:hover": { borderColor: "primary.light", bgcolor: "primary.50" },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        textTransform="uppercase"
        letterSpacing={0.4}
        display="block"
        fontSize="0.6rem"
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        fontSize="0.8rem"
        color={value != null && value !== "" ? "text.primary" : "text.disabled"}
      >
        {value != null && value !== "" ? value : "N/A"}
      </Typography>
    </Box>
  </Grid>
);

const ViewCoolerProfile = ({ profile }) => {
  if (!profile) return null;

  const formatDate = (val) => (val ? new Date(val).toLocaleString() : null);

  const dimensions =
    profile.shippingWidth || profile.shippingHeight || profile.shippingDepth
      ? `${profile.shippingWidth || "-"} × ${profile.shippingHeight || "-"} × ${profile.shippingDepth || "-"}`
      : null;

  const weight =
    profile.shippingWeight
      ? `${profile.shippingWeight} ${profile.shippingWeightUnit || ""}`.trim()
      : null;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700} letterSpacing={-0.3}>
            Cooler Profile
          </Typography>
          <Typography variant="caption" color="text.secondary">
            — Shipping Record
          </Typography>
        </Box>
        <Chip
          label="Cooler Profile"
          color="primary"
          variant="outlined"
          size="small"
          icon={<AcUnitIcon sx={{ fontSize: 14 }} />}
        />
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      <Grid container spacing={1}>
        {/* Identity */}
        <SectionHeader
          icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
          title="Profile Information"
        />
        <FieldCard label="Profile Name" value={profile.profileName} />
        <FieldCard label="Cooler Size" value={profile.coolerSize} />

        {/* Weight */}
        <SectionHeader
          icon={<ScaleIcon sx={{ fontSize: 14 }} />}
          title="Weight"
        />
        <FieldCard label="Shipping Weight" value={weight} />
        <FieldCard label="Weight Unit" value={profile.shippingWeightUnit} />

        {/* Dimensions */}
        <SectionHeader
          icon={<StraightenIcon sx={{ fontSize: 14 }} />}
          title="Shipping Dimensions"
        />
        <FieldCard label="Width" value={profile.shippingWidth} />
        <FieldCard label="Height" value={profile.shippingHeight} />
        <FieldCard label="Depth" value={profile.shippingDepth} />
        <FieldCard label="W × H × D" value={dimensions} />

        {/* Audit Trail */}
        <SectionHeader
          icon={<HistoryIcon sx={{ fontSize: 14 }} />}
          title="Audit Trail"
        />
        <FieldCard label="Created At" value={formatDate(profile.createdAt)} />
        <FieldCard label="Created By" value={profile.createdBy} />
        {profile.updatedAt && (
          <FieldCard label="Updated At" value={formatDate(profile.updatedAt)} />
        )}
        {profile.updatedBy && (
          <FieldCard label="Updated By" value={profile.updatedBy} />
        )}
      </Grid>
    </Paper>
  );
};

export default ViewCoolerProfile;